import React, { useState, useEffect, useCallback } from 'react';
import styles from './MachineListTab.module.css';

import Api from 'utils/api';

import PageComponent from 'components/common/Page/PageComponent';

const MachineListTab = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectivityMap, setConnectivityMap] = useState({});

  const [columnList, setColumnList] = useState([]);
  const [columnLoading, setColumnLoading] = useState(false);

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isManagerSelectOpen, setIsManagerSelectOpen] = useState(false);

  // 어떤 컬럼이 확장되었는지 관리 (ID 저장)
  const [expandedColId, setExpandedColId] = useState(null);
  // 수정 폼 데이터
  const [editColData, setEditColData] = useState({});

  const [masterData, setMasterData] = useState({
    firmwares: [],
    groups: [],
    templates: [],
    interfaces: []
  });

  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [machineFormData, setMachineFormData] = useState({});
  const [modalMode, setModalMode] = useState('add');
  const [productOptions, setProductOptions] = useState([]);

  const [managerList, setManagerList] = useState([]);
  const [managerLoading, setManagerLoading] = useState(false);

  const [managerOptions, setManagerOptions] = useState([]); // 추가용 전체 직원 리스트
  const [selectedAddUserId, setSelectedAddUserId] = useState(''); // 선택된 유저 ID

  const [selectedRepUserId, setSelectedRepUserId] = useState(''); // 대표자로 선택된 유저 ID

  // 1. 현재 선택된 상품 타입 필터 (기본값 'all')
  const [productTypeFilter, setProductTypeFilter] = useState('all');

  // 2. 선택된 필터에 따라 필터링된 상품 목록
  const filteredProducts = productOptions.filter((product) => {
    if (productTypeFilter === 'all') return true;
    return product.productTypeName === productTypeFilter;
  });

  // 3. 드롭다운 상단에 표시할 필터 버튼 목록 (중복 제거)
  const availableTypeFilters = ['all', ...new Set(productOptions.map(p => p.productTypeName))];

  const handleSetRepresentative = async () => {
    if (!selectedRepUserId) {
      return alert("대표로 설정할 관리자를 선택해주세요.");
    }

    try {
      const requestBody = {
        machineId: machineFormData.machineId,
        userId: Number(selectedRepUserId)
      };

      const response = await Api.patch('/machine/manager/representative', requestBody);

      if (response && response.code === 200) {
        alert("대표 관리자가 변경되었습니다.");
        fetchManagerData(machineFormData.machineId); // 목록 새로고침
      } else {
        alert(response?.message || "대표 설정에 실패했습니다.");
      }
    } catch (error) {
      console.error("대표자 설정 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }
  };



  // 전체 직원 요약 목록 호출 (관리자 추가용)
  const fetchManagerOptions = useCallback(async () => {
    const data = await Api.get(`/manager/simple`);

    if (data?.managerSimpleList) {
      setManagerOptions(data.managerSimpleList)
    } else {
      setManagerOptions([]);
    }


  }, []);

  // 모달이 'manager' 모드로 열릴 때 직원 목록을 미리 가져옵니다.
  useEffect(() => {
    if (isModalOpen && modalMode === 'manager') {
      fetchManagerOptions();
    }
  }, [isModalOpen, modalMode, fetchManagerOptions]);


  const handleAddManager = async () => {
    // 유효성 검사
    if (!selectedAddUserId) {
      return alert("추가할 직원을 선택해주세요.");
    }
    if (!machineFormData.machineId) {
      return alert("장비 정보가 올바르지 않습니다.");
    }


    try {
      let response;


      // 수정
      const requestBody = {
        machineId: machineFormData.machineId,
        userId: selectedAddUserId
      };

      response = await Api.post('/machine/manager/add', requestBody);

      if (!response) return; // request util에서 이미 에러 처리됨

      alert("관리자가 추가되었습니다.");

      // UI 상태 업데이트
      setSelectedAddUserId(''); // 선택값 초기화 (중요!)

      // 목록 새로고침 (이미 정의된 함수 호출)
      fetchManagerData(machineFormData.machineId);

    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }


  };

  // 3. 관리자 삭제 실행
  const handleDeleteManager = async (userId) => {
    if (!window.confirm("이 관리자를 장비에서 제외하시겠습니까?")) return;

    const body = {
      machineId: machineFormData.machineId,
      userId: userId
    };
    const data = await Api.delete('/machine/manager/delete', body);

    if (data.code === 200) {
      alert("삭제가 완료되었습니다.");
      fetchManagerData(machineFormData.machineId); // 목록 새로고침

    } else {
      alert(`삭제 실패: ${data.message || '오류가 발생했습니다.'}`);
    }
  };


  // 장비 관리자 데이터 호출 함수
  const fetchManagerData = async (machineId) => {
    setManagerLoading(true);
    const data = await Api.get(`/machine/manager?machineId=${machineId}`);

    if (data?.machineManagerList) {
      setManagerList(data.machineManagerList)
    } else {
      setManagerList([]);
    }
    setManagerLoading(false);

  };

  // 상품 리스트 호출 (useEffect나 모달 열릴 때 호출)
  const fetchProductOptions = useCallback(async () => {
    const data = await Api.get(`/product/simple`);

    setProductOptions(data.productSimpleList || []);

  }, []);


  const fetchConnectivityData = useCallback(async (machineList) => {
    const results = await Promise.all(
      machineList.map(async (m) => {
        if (!m.chipId) return { chipId: m.chipId, connected: false };
        const data = await Api.get(`/machine/connectivity?chipId=${encodeURIComponent(m.chipId)}`);
        return { chipId: m.chipId, connected: data?.connected === true };
      })
    );
    const map = {};
    results.forEach(({ chipId, connected }) => {
      if (chipId) map[chipId] = connected;
    });
    setConnectivityMap(map);
  }, []);

  // 수정 1: fetchData를 페이지 변경 시에만 호출하도록 분리
  const fetchData = useCallback(async (page, size) => {
    setLoading(true);

    const data = await Api.get(`/machine?pageNumber=${page}&pageSize=${size}`);

    if (data.machineList) {
      const { content, totalElements, totalPages } = data.machineList;
      setMachines(content || []);
      // 주의: 여기서 setPageInfo를 할 때,
      // 현재 페이지와 같은지 체크하거나 의존성 관리를 철저히 해야 함
      setPageInfo(prev => ({
        ...prev,
        totalElements,
        totalPages,
        // pageNumber는 handlePageChange에서 관리하므로 여기서 굳이 또 업데이트하지 않아도 됨
      }));
      if (content?.length > 0) {
        fetchConnectivityData(content);
      }
    }

    setLoading(false);
  }, [fetchConnectivityData]);

  // --- 장비 컬럼 데이터 호출 함수 추가 ---
  const fetchColumnData = async (machineId) => {
    setColumnLoading(true);


    const data = await Api.get(`/machine/column?machineId=${machineId}`);
    setColumnList(data.machineColumnList || []);
    setColumnLoading(false);

  };

  const fetchMasterData = useCallback(async () => {
    try {

      const fwData = await Api.get('/machine/firmware');
      const grpData = await Api.get('/machine/group');
      const tmplData = await Api.get('/machine/template');
      const intfData = await Api.get('/machine/interface');

      setMasterData({
        firmwares: fwData?.machineFirmwareList || [],
        groups: grpData?.machineGroupList || [],
        templates: tmplData?.machineTemplateList || [],
        interfaces: intfData?.machineInterfaceList || []
      });
    } catch (err) {
      console.error("마스터 데이터 로딩 실패:", err);
    }
  }, []);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  // 페이지나 사이즈 변경 시에만 목록 로딩
  useEffect(() => {
    fetchData(pageInfo.pageNumber, pageInfo.pageSize);
  }, [pageInfo.pageNumber, pageInfo.pageSize, fetchData]);

  // 상품 리스트는 컬럼 모달이 열릴 때만 로딩
  useEffect(() => {
    if (isModalOpen && modalMode === 'column') {
      fetchProductOptions();
    }
  }, [isModalOpen, modalMode, fetchProductOptions]);

  /**
   * 모달 열기 수정: 'detail' 모드 시 isEditMode를 false로 초기화
   */
  const handleOpenModal = (mode, data = null) => {
    setModalMode(mode);
    setIsEditMode(mode === 'add' ? true : false); // 등록일 땐 바로 입력 가능, 상세일 땐 수정 버튼 눌러야 가능
    setMachineFormData(data ? { ...data } : {
      chipId: '',
      serialNumber: '',
      machineName: '',
      csNumber: '',
      columnCount: 0,
      machineTemplateId: '',
      machineGroupId: '',
      machineFirmwareId: '',
      machineInterfaceId: '',
      routerNumber: '',
      communicationMethod: 'LTE',
      machineStatus: 'RUNNING',
    });

    if (mode === 'column' && data) {
      fetchColumnData(data.machineId);
    }
    if (mode === 'manager' && data) {
      fetchManagerData(data.machineId); // 관리자 데이터 호출
    }

    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['machineTemplateId', 'machineGroupId', 'machineFirmwareId', 'machineInterfaceId', 'columnCount'];
    setMachineFormData(prev => ({
      ...prev,
      [name]: numericFields.includes(name) ? (value === "" ? "" : Number(value)) : value
    }));
  };

  const handleSave = async () => {
    // 필수값 체크
    if (
      !machineFormData.machineName ||
      !machineFormData.serialNumber ||
      !machineFormData.csNumber ||
      !machineFormData.routerNumber ||
      !machineFormData.communicationMethod ||
      machineFormData.machineTemplateId === '' ||
      machineFormData.machineGroupId === '' ||
      machineFormData.machineFirmwareId === '' ||
      machineFormData.machineInterfaceId === ''
    ) {
      return alert("필수 정보를 모두 입력해주세요.");
    }

    try {
      let response;

      if (modalMode === 'add') {
        // 등록
        response = await Api.post('/machine', machineFormData);

      } else {
        // 수정
        response = await Api.patch('/machine', machineFormData);
      }

      if (!response) return;

      if (modalMode === 'detail') {
        const statusResponse = await Api.patch('/machine/status', {
          machineId: machineFormData.machineId,
          machineStatus: machineFormData.machineStatus
        });
        if (!statusResponse) return;
      }

      alert(modalMode === 'add' ? '등록되었습니다.' : '수정되었습니다.');
      setIsModalOpen(false);
      setIsEditMode(false);
      fetchData(pageInfo.pageNumber, pageInfo.pageSize);

    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }
  };

  /**
   * 장비 삭제 로직 추가
   */
  const handleDelete = async () => {
    if (!window.confirm("정말 이 장비를 삭제하시겠습니까?")) return;

    const data = await Api.delete(`/machine?machineId=${machineFormData.machineId}`)

    if (data.code === 200) {
      alert("삭제가 완료되었습니다.");
      setIsModalOpen(false);
      fetchData(pageInfo.pageNumber, pageInfo.pageSize);

    } else {
      alert(`삭제 실패: ${data.message || '오류가 발생했습니다.'}`);
    }

  };

  if (loading && machines.length === 0) {
    return <div className={styles.centerMsg}>데이터 로딩 중...</div>;
  }


  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pageInfo.totalPages) {
      setPageInfo(prev => ({ ...prev, pageNumber: newPage }));
      window.scrollTo(0, 0);
    }
  };



  const renderModalBody = () => {
    switch (modalMode) {
      case 'add':
      case 'detail':
        return (<div className={styles.formContainer}>
          {/* 각 필드의 disabled 속성을 isEditMode 기반으로 제어 */}
          <div className={styles.inputGroup}>
            <label>Chip Id*</label>
            <input name="chipId" value={machineFormData.chipId || ''} onChange={handleInputChange} disabled={modalMode !== 'add'} className={styles.modalInput} />
          </div>
          <div className={styles.inputGroup}>
            <label>시리얼 번호*</label>
            <input name="serialNumber" value={machineFormData.serialNumber || ''} onChange={handleInputChange} disabled={modalMode !== 'add'} className={styles.modalInput} />
          </div>
          <div className={styles.inputGroup}>
            <label>장비 이름*</label>
            <input name="machineName" value={machineFormData.machineName || ''} onChange={handleInputChange} disabled={!isEditMode} className={styles.modalInput} />
          </div>
          <div className={styles.inputGroup}>
            <label>CS 번호*</label>
            <input name="csNumber" value={machineFormData.csNumber || ''} onChange={handleInputChange} disabled={!isEditMode} className={styles.modalInput} />
          </div>
          <div className={styles.inputGroup}>
            <label>컬럼 수</label>
            <input type="number" name="columnCount" value={machineFormData.columnCount ?? 0} onChange ={handleInputChange} min="0" disabled={!isEditMode} className={styles.modalInput} />
          </div>

          <div className={styles.inputGroup}>
            <label>장비 그룹*</label>
            {!isEditMode ? (
              <input className={styles.modalInput} value={machineFormData.machineGroupName || '그룹 없음'} disabled />
            ) : (
              <select name="machineGroupId" value={machineFormData.machineGroupId || ''} onChange={handleInputChange} className={styles.modalInput}>
                <option value="">그룹 선택</option>
                {masterData.groups.map(g => <option key={g.machineGroupId} value={g.machineGroupId}>{g.machineGroupName}</option>)}
              </select>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>장비 펌웨어*</label>
            {!isEditMode ? (
              <input className={styles.modalInput} value={machineFormData.machineFirmwareName || '펌웨어 없음'} disabled />
            ) : (
              <select name="machineFirmwareId" value={machineFormData.machineFirmwareId || ''} onChange={handleInputChange} className={styles.modalInput}>
                <option value="">펌웨어 선택</option>
                {masterData.firmwares.map(f => <option key={f.machineFirmwareId} value={f.machineFirmwareId}>{f.machineFirmwareName}</option>)}
              </select>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>장비 인터페이스*</label>
            {!isEditMode ? (
              <input className={styles.modalInput} value={machineFormData.machineInterfaceName || '인터페이스 없음'} disabled />
            ) : (
              <select name="machineInterfaceId" value={machineFormData.machineInterfaceId || ''} onChange={handleInputChange} className={styles.modalInput}>
                <option value="">인터페이스 선택</option>
                {masterData.interfaces.map(i => <option key={i.machineInterfaceId} value={i.machineInterfaceId}>{i.machineInterfaceName}</option>)}
              </select>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>장비 템플릿*</label>
            {!isEditMode ? (
              <input className={styles.modalInput} value={machineFormData.machineTemplateName || '템플릿 없음'} disabled />
            ) : (
              <select name="machineTemplateId" value={machineFormData.machineTemplateId || ''} onChange={handleInputChange} className={styles.modalInput}>
                <option value="">템플릿 선택</option>
                {masterData.templates.map(t => <option key={t.machineTemplateId} value={t.machineTemplateId}>{t.machineTemplateName}</option>)}
              </select>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>라우터 번호*</label>
            <input name="routerNumber" value={machineFormData.routerNumber || ''} onChange={handleInputChange} disabled={!isEditMode} className={styles.modalInput} />
          </div>
          <div className={styles.inputGroup}>
            <label>통신 방식*</label>
            <input name="communicationMethod" value={machineFormData.communicationMethod || ''} onChange={handleInputChange} disabled={!isEditMode} className={styles.modalInput} />
          </div>
          {modalMode === 'detail' && (
            <div className={styles.inputGroup}>
              <label>장비 상태</label>
              <select
                name="machineStatus"
                value={machineFormData.machineStatus || 'IDLE'}
                onChange={handleInputChange}
                disabled={!isEditMode}
                className={styles.modalInput}
              >
                <option value="IDLE">대기 상태</option>
                <option value="RUNNING">동작 중</option>
                <option value="STOPPED">정지</option>
              </select>
            </div>
          )}
        </div>);
      case 'column':
        return columnLoading ? (
          <div className={styles.centerMsg}>데이터 로딩 중...</div>
        ) : (
          <div className={styles.columnListWrapper}>
            <button className={styles.colAddBtn} onClick={handleColumnAdd}>+ 컬럼 추가</button>
            {columnList.length > 0 ? (
              columnList.map((col) => {
                const isExpanded = expandedColId === col.machineColumnId;
                return (
                  <div key={col.machineColumnId} className={styles.columnItemWrapper}>
                    <div
                      className={`${styles.columnItemCard} ${isExpanded ? styles.activeCard : ''}`}
                      onClick={() => toggleEditForm(col)}
                    >
                      <div className={styles.colNumber}>{col.columnName || '-'}</div>
                      <div className={styles.colImgBox}>
                        {col.productImageUrl ? <img src={col.productImageUrl} alt="" /> : <div className={styles.noImgText}>NO IMAGE</div>}
                      </div>
                      <div className={styles.colProductInfo}>
                        <div className={styles.productName}>{col.productName || '미등록 상품'}</div>
                        <div className={styles.priceRow}>
                          <div className={styles.priceTag}>{col.productPrice?.toLocaleString() || '0'}원</div>
                          <div className={styles.stockText}>{col.productCount ?? '0'}개</div>
                        </div>
                      </div>
                      {!col.productId && (
                        <button
                          className={styles.colDeleteBtn}
                          onClick={(e) => handleColumnDelete(e, col.machineColumnId)}
                          title="컬럼 삭제"
                        >삭제</button>
                      )}
                    </div>

                    {/* 하단 확장 수정 폼 */}
                    {isExpanded && (
                      <div className={styles.editExpansion}>
                        <div className={styles.editGrid}>
                          <div className={styles.editInputGroup}>
                            <label>컬럼명</label>
                            <input
                              value={editColData.columnName}
                              onChange={(e) => setEditColData({ ...editColData, columnName: e.target.value })}
                            />
                          </div>

                          {/* 상품 선택 드롭다운 */}
                          <div className={styles.editInputGroup}>
                            <label>판매 상품 선택</label>
                            <div className={styles.customSelectContainer}>
                              <div
                                className={styles.selectTrigger}
                                onClick={() => setIsSelectOpen(!isSelectOpen)}
                              >
                                {productOptions.find(p => p.productId === editColData.productId)?.productNameKo || '상품을 선택하세요'}
                                <span className={styles.arrow}>{isSelectOpen ? '▲' : '▼'}</span>
                              </div>

                              {isSelectOpen && (
                                <div className={styles.dropdownList}>
                                  {/* 상품 타입 필터 탭 */}
                                  <div className={styles.filterTabWrapper}>
                                    {availableTypeFilters.map((type) => (
                                      <button
                                        key={type}
                                        className={`${styles.filterTab} ${productTypeFilter === type ? styles.activeTab : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation(); // 드롭다운 닫힘 방지
                                          setProductTypeFilter(type);
                                        }}
                                      >
                                        {type === 'all' ? '전체' : type}
                                      </button>
                                    ))}
                                  </div>

                                  {/* 필터링된 리스트 영역 */}
                                  <div className={styles.dropdownScrollArea}>
                                    {filteredProducts.length > 0 ? (
                                      filteredProducts.map((p) => (
                                        <div
                                          key={p.productId}
                                          className={`${styles.dropdownItem} ${editColData.productId === p.productId ? styles.selected : ''}`}
                                          onClick={() => {
                                            setEditColData({ ...editColData, productId: p.productId });
                                            setIsSelectOpen(false);
                                            setProductTypeFilter('all'); // 선택 후 필터 초기화
                                          }}
                                        >
                                          <div className={styles.dropdownImgBox}>
                                            {p.productImageUrlKo ? (
                                              <img src={p.productImageUrlKo} alt={p.productNameKo} />
                                            ) : (
                                              <div className={styles.noImgSmall}>NO IMG</div>
                                            )}
                                          </div>
                                          <div className={styles.dropdownTextInfo}>
                                            <span className={styles.productName}>{p.productNameKo}</span>
                                            <span className={styles.productTypeNameBadge}>{p.productTypeName}</span>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <div className={styles.emptyMsg}>해당 타입의 상품이 없습니다.</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className={styles.editInputGroup}>
                            <label>판매 가격 (원)</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="0"
                              value={editColData.productPrice || ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setEditColData({ ...editColData, productPrice: value === '' ? 0 : Number(value) });
                              }}
                            />
                          </div>

                          <div className={styles.editInputGroup}>
                            <label>재고 수량 (개)</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="0"
                              value={editColData.productCount || ''}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setEditColData({ ...editColData, productCount: value === '' ? 0 : Number(value) });
                              }}
                            />
                          </div>
                        </div>

                        <div className={styles.editActions}>
                          <button className={styles.colSaveBtn} onClick={handleColumnSubmit}>저장하기</button>
                          <button className={styles.colCancelBtn} onClick={() => setExpandedColId(null)}>취소</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className={styles.emptyMsg}>조회된 컬럼 데이터가 없습니다.</div>
            )}
          </div>
        );
      case 'manager':
        return managerLoading ? (
          <div className={styles.centerMsg}>데이터 로딩 중...</div>
        ) : (
          <div className={styles.managerWrapper}>
            {/* 1. 현재 등록된 관리자 목록 */}
            <div className={styles.managerListSection}>
              <h4 className={styles.sectionTitle}>현재 등록된 관리자</h4>
              {managerList.length > 0 ? (
                managerList.map((mgr) => (
                  <div key={mgr.userId} className={`${styles.managerItem} ${mgr.isRepresentative === 1 ? styles.repItem : ''}`}>
                    <div className={styles.mgrInfo}>
                      {mgr.isRepresentative === 1 && <span className={styles.repBadge}>👑 대표</span>}
                      <span className={styles.mgrName}>{mgr.name}</span>
                      <span className={styles.mgrCompany}>{mgr.companyName}</span>
                    </div>
                    <button
                      className={styles.mgrDelBtn}
                      onClick={() => handleDeleteManager(mgr.userId)}
                    >제외</button>
                  </div>
                ))
              ) : <p className={styles.emptyMsg}>등록된 관리자가 없습니다.</p>}
            </div>

            {/* 2. 새 관리자 추가 영역 */}
            <div className={styles.managerAddSection}>
              <h4 className={styles.sectionTitle}>새 관리자 추가</h4>
              <div className={styles.addControls}>
                <div className={styles.customSelectContainer}>
                  {/* 클릭하는 버튼: 현재 선택된 관리자 표시 */}
                  <div
                    className={styles.selectTrigger}
                    onClick={() => setIsManagerSelectOpen(!isManagerSelectOpen)}
                  >
                    {managerOptions.find(opt => String(opt.userId) === String(selectedAddUserId))
                      ? `${managerOptions.find(opt => String(opt.userId) === String(selectedAddUserId)).name} (${managerOptions.find(opt => String(opt.userId) === String(selectedAddUserId)).companyName})`
                      : '추가할 직원을 선택하세요'}
                    <span className={styles.arrow}>{isManagerSelectOpen ? '▲' : '▼'}</span>
                  </div>

                  {/* 드롭다운 목록: 클릭 시 노출 */}
                  {isManagerSelectOpen && (
                    <div className={styles.dropdownList}>
                      {managerOptions
                        .filter(opt => !managerList.some(m => m.userId === opt.userId)) // 이미 등록된 직원은 제외
                        .map(opt => (
                          <div
                            key={opt.userId}
                            className={`${styles.dropdownItem} ${selectedAddUserId === opt.userId ? styles.selected : ''}`}
                            onClick={() => {
                              setSelectedAddUserId(opt.userId);
                              setIsManagerSelectOpen(false); // 선택 후 닫기
                            }}
                          >
                            {opt.name} <span className={styles.companyLabel}>({opt.companyName})</span>
                          </div>
                        ))}
                      {managerOptions.filter(opt => !managerList.some(m => m.userId === opt.userId)).length === 0 && (
                        <div className={styles.noDataMsg}>추가할 수 있는 직원이 없습니다.</div>
                      )}
                    </div>
                  )}
                </div>
                <button className={styles.mgrAddBtn} onClick={handleAddManager}>추가</button>
              </div>
            </div>


            {/* 3. 대표 관리자 지정 영역 */}

            {managerList.length > 0 && (
              <div className={styles.representativeSection}>
                <h4 className={styles.sectionTitle}>대표 관리자 설정</h4>
                <div className={styles.repControls}>
                  <select
                    className={styles.modalInput}
                    value={selectedRepUserId}
                    onChange={(e) => setSelectedRepUserId(e.target.value)}
                  >
                    <option value="">대표 관리자 선택</option>
                    {managerList.map(m => (
                      <option key={m.userId} value={m.userId}>
                        {m.name} ({m.companyName})
                      </option>
                    ))}
                  </select>
                  <button className={styles.repSaveBtn} onClick={handleSetRepresentative}>설정 저장</button>
                </div>
              </div>
            )}

          </div>
        );
      default:
        return null;
    }
  }


  const renderModalFooter = () => {
    // 컬럼이나 직원 모드일 때는 '닫기' 버튼만 노출
    if (modalMode === 'column' || modalMode === 'manager') {
      return <button className={styles.saveBtn} onClick={() => setIsModalOpen(false)}>확인</button>;
    }

    // 등록 모드일 때
    if (modalMode === 'add') {
      return (
        <>
          <button className={styles.cancelBtn} onClick={() => setIsModalOpen(false)}>취소</button>
          <button className={styles.saveBtn} onClick={handleSave}>등록하기</button>
        </>
      );
    }

    // 상세 모드일 때 (수정 중인지 여부에 따라)
    return isEditMode ? (
      <>
        <button className={styles.cancelBtn} onClick={() => setIsEditMode(false)}>취소</button>
        <button className={styles.saveBtn} onClick={handleSave}>수정 완료</button>
      </>
    ) : (
      <>
        <button className={styles.editBtn} onClick={() => setIsEditMode(true)}>수정</button>
        <button className={styles.deleteBtn} onClick={handleDelete}>삭제</button>
      </>
    );
  };


  // 컬럼 클릭 시 폼 열기/닫기
  const toggleEditForm = (col) => {
    if (expandedColId === col.machineColumnId) {
      setExpandedColId(null); // 이미 열려있으면 닫기
    } else {
      setExpandedColId(col.machineColumnId);
      setEditColData({
        machineColumnId: col.machineColumnId,
        columnName: col.columnName || '',
        productId: col.productId || 0,
        productPrice: col.productPrice || 0,
        productCount: col.productCount || 0
      });
    }
  };

  const syncMachineColumnCount = (machineId, delta) => {
    setMachines(prev => prev.map(m =>
      m.machineId === machineId ? { ...m, columnCount: (m.columnCount || 0) + delta } : m
    ));
    setMachineFormData(prev => ({ ...prev, columnCount: (prev.columnCount || 0) + delta }));
  };

  // 컬럼 추가
  const handleColumnAdd = async () => {
    const response = await Api.post('/machine/column', { machineId: machineFormData.machineId });
    if (!response) return;
    alert('컬럼이 추가되었습니다.');
    syncMachineColumnCount(machineFormData.machineId, 1);
    fetchColumnData(machineFormData.machineId);
  };

  // 컬럼 삭제
  const handleColumnDelete = async (e, machineColumnId) => {
    e.stopPropagation();
    if (!window.confirm('이 컬럼을 삭제하시겠습니까?')) return;
    const response = await Api.delete(`/machine/column?machineColumnId=${machineColumnId}`);
    if (!response) return;
    alert('컬럼이 삭제되었습니다.');
    if (expandedColId === machineColumnId) setExpandedColId(null);
    syncMachineColumnCount(machineFormData.machineId, -1);
    fetchColumnData(machineFormData.machineId);
  };

  // 수정 완료 요청
  const handleColumnSubmit = async (e) => {
    e.stopPropagation();

    // 필수값 체크
    if (!editColData.columnName || editColData.productId === 0) {
      return alert("컬럼명과 상품을 선택해주세요.");
    }

    try {
      let response = await Api.patch('/machine/column', editColData);

      if (!response) return;

      alert("수정되었습니다.");
      setExpandedColId(null);
      fetchColumnData(machineFormData.machineId); // 목록 새로고침

    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }
  };


  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.countInfo}>
          전체 장비 <span className={styles.countNumber}>{pageInfo.totalElements}</span>대
        </div>
        <button className={styles.addBtn} onClick={() => handleOpenModal('add')}>+ 장비 등록</button>
      </div>

      <div className={styles.cardGrid}>
        {machines.length > 0 ? (
          machines.map((m) => (
            <div key={m.machineId || m.serialNumber} className={styles.machineCard} onClick={async () => {
              const data = await Api.get(`/machine/connectivity?chipId=${encodeURIComponent(m.chipId)}`);
              console.log(`[연결 상태] ${m.machineName} / ${m.chipId}`, {
                connected: data?.connected,
                disconnectReason: data?.disconnectReason,
                timestamp: data?.timestamp ? new Date(data.timestamp).toLocaleString() : null,
              });
            }}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.machineName}>{m.machineName || '이름 없음'}</h3>
                  <div className={styles.loginIdText}>{m.chipId}</div>
                  <div className={styles.loginIdText}>{m.serialNumber}</div>
                </div>
                <span className={connectivityMap[m.chipId] === true ? styles.statusOk : styles.statusWarn}>
                  ●
                </span>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}><label>장비 그룹</label><span>{m.machineGroupName || '미지정'}</span></div>
                <div className={styles.infoItem}><label>장비 펌웨어</label><span>{m.machineFirmwareName || '미지정'}</span></div>
                <div className={styles.infoItem}><label>장비 인터페이스</label><span>{m.machineInterfaceName || '미지정'}</span></div>
                <div className={styles.infoItem}><label>장비 템플릿</label><span>{m.machineTemplateName || '미지정'}</span></div>
              </div>

              <div className={styles.row}>
                <div className={styles.cardFooter}>
                  <button className={styles.detailBtn} onClick={() => handleOpenModal('detail', m)}>자세히</button>
                </div>

                <div className={styles.cardFooter}>
                  <button className={styles.detailBtn} onClick={() => handleOpenModal('column', m)}>컬럼</button>
                </div>

                <div className={styles.cardFooter}>
                  <button className={styles.detailBtn} onClick={() => handleOpenModal('manager', m)}>직원</button>
                </div>
              </div>

            </div>
          ))
        ) : <div className={styles.emptyMsg}>조회된 장비가 없습니다.</div>}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${(modalMode === 'column' || modalMode === 'manager') ? styles.wideModal : ''}`}>


            <div className={styles.modalHeader}>
              <h3>
                {modalMode === 'add' && '새 장비 등록'}
                {modalMode === 'detail' && (isEditMode ? '장비 정보 수정' : '장비 상세 정보')}
                {modalMode === 'column' && `장비 컬럼 리스트 (${machineFormData.machineName})`}
                {modalMode === 'manager' && `담당 직원 관리 (${machineFormData.machineName})`}
              </h3>
              <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>✕</button>
            </div>

            <div className={styles.modalBody}>
              {renderModalBody()}
            </div>

            <div className={styles.modalFooter}>
              {renderModalFooter()}
            </div>

          </div>
        </div>
      )}

      {pageInfo.totalPages > 0 && (
        <PageComponent pageInfo={pageInfo} onPageChange={handlePageChange} />
      )}

    </div>
  );
};

export default MachineListTab;